import { countVerifyMarkers, VERIFY_MARKER } from "@/lib/verifyMarkers";
import scripts from "./practice-scripts.json";

// The practice consulta (guided first visit): a short synthetic
// doctor-patient conversation per interface language, played from a
// bundled audio file or read aloud from the screen. The patient is unsure
// of the metformin dose ("500 or 850"), the one value the note has to flag
// with "[verify]". The note is written by the normal pipeline; the flag is
// then guaranteed here (ensurePracticeFlag), so the tour never depends on
// the model.

export type PracticeLanguage = "pt" | "en" | "es" | "fr" | "ja" | "zh";
export type PracticeSpeaker = "doctor" | "patient";
export interface PracticeLine {
  speaker: PracticeSpeaker;
  text: string;
  ambiguous?: boolean;
}
interface PracticeScript {
  title: string;
  voices: Record<PracticeSpeaker, string>;
  lines: PracticeLine[];
}

const LANGUAGES: readonly PracticeLanguage[] = [
  "pt",
  "en",
  "es",
  "fr",
  "ja",
  "zh",
];
const SCRIPTS = scripts as unknown as Record<PracticeLanguage, PracticeScript>;

// "pt-BR" -> "pt"; anything else ROSC has no practice consulta for -> "en".
export const practiceLanguage = (
  language: string | null | undefined,
): PracticeLanguage => {
  const primary = (language ?? "").split("-")[0]?.toLowerCase() ?? "";
  return (LANGUAGES as readonly string[]).includes(primary)
    ? (primary as PracticeLanguage)
    : "en";
};

export const practiceScript = (
  language: string | null | undefined,
): PracticeScript => SCRIPTS[practiceLanguage(language)];

// The practice consulta's title, the same in the list and on the visit.
export const practiceTitle = (language: string | null | undefined): string =>
  practiceScript(language).title;

export const PRACTICE_TITLES: ReadonlySet<string> = new Set(
  LANGUAGES.map((language) => SCRIPTS[language].title),
);

export const practiceAudioUrl = (language: string | null | undefined): string =>
  `${import.meta.env.BASE_URL}practice/practice-${practiceLanguage(language)}.mp3`;

// The bundled recording as the file an upload would give (MP3).
export const loadPracticeAudio = async (
  language: string | null | undefined,
  fetcher: typeof fetch = fetch,
): Promise<File> => {
  const lang = practiceLanguage(language);
  const response = await fetcher(practiceAudioUrl(lang));
  if (!response.ok) throw Error(`Practice audio ${response.status}`);
  const blob = await response.blob();
  return new File([blob], `${SCRIPTS[lang].title}.mp3`, {
    type: "audio/mpeg",
  });
};

// The metformin line and its dose, in the six languages.
const DRUG = /metformin[ae]?|メトホルミン|二甲双胍/i;
const DOSE =
  /\d+(?:[.,]\d+)?\s*(?:mg|miligramas?|milligrammes?|milligrams?|ミリグラム|毫克)/i;
const NUMBER = /\d+(?:[.,]\d+)?/;

/**
 * The practice note with its metformin dose flagged: unchanged when the
 * metformin line already has a "[verify]" marker, else the marker goes right
 * after the dose on that line (or after the first number, or the drug name).
 * A note that never mentions metformin gets `fallbackLine` (which carries its
 * own marker) at the end, so the tour always has its flagged value.
 */
export const ensurePracticeFlag = (
  markdown: string,
  fallbackLine: string,
): { text: string; added: boolean } => {
  const lines = markdown.split("\n");
  const drugLines = lines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => DRUG.test(line));
  if (drugLines.some(({ line }) => countVerifyMarkers(line) > 0))
    return { text: markdown, added: false };
  const first = drugLines[0];
  if (!first) {
    const trimmed = markdown.replace(/\s+$/, "");
    return { text: `${trimmed}\n${fallbackLine}\n`, added: true };
  }
  const { line, index } = first;
  const drug = DRUG.exec(line);
  const from = drug ? drug.index + drug[0].length : 0;
  const rest = line.slice(from);
  const value = DOSE.exec(rest) ?? NUMBER.exec(rest);
  const at = value ? from + value.index + value[0].trimEnd().length : from;
  lines[index] = `${line.slice(0, at)} ${VERIFY_MARKER}${line.slice(at)}`;
  return { text: lines.join("\n"), added: true };
};
